import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { 
  Plus, 
  Trash2, 
  GripVertical, 
  MapPin,
  Route
} from 'lucide-react';
import AddressInput from './AddressInput';

/**
 * Composant simplifié pour gérer les étapes intermédiaires.
 * Design minimaliste - juste adresse + instructions optionnelles.
 */
const MultiStopInput = ({ 
  stops = [], 
  onStopsChange, 
  maxStops = 10,
  disabled = false 
}) => {
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Ajouter une nouvelle étape
  const addStop = () => {
    if (stops.length >= maxStops) return;

    const newStop = {
      order: stops.length + 1,
      address: '',
      city: '',
      country: 'CI',
      coordinates: null,
      instructions: ''
    };

    onStopsChange([...stops, newStop]);
  };

  // Supprimer une étape
  const removeStop = (index) => {
    const newStops = stops.filter((_, i) => i !== index);
    const reorderedStops = newStops.map((stop, i) => ({
      ...stop,
      order: i + 1
    }));
    onStopsChange(reorderedStops);
  };

  // Mettre à jour l'adresse avec les coordonnées
  const handleAddressChange = (index, addressText, coordinates) => {
    const newStops = [...stops];
    
    // Parser l'adresse pour extraire la ville
    let city = '';
    if (addressText) {
      const parts = addressText.split(',');
      if (parts.length >= 2) {
        city = parts[parts.length - 2]?.trim() || parts[0]?.trim();
      } else {
        city = parts[0]?.trim();
      }
    }

    newStops[index] = {
      ...newStops[index],
      address: addressText,
      city: city,
      coordinates: coordinates || null
    };
    onStopsChange(newStops);
  };

  // Mettre à jour les instructions
  const handleInstructionsChange = (index, value) => {
    const newStops = [...stops];
    newStops[index] = {
      ...newStops[index],
      instructions: value
    };
    onStopsChange(newStops);
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === targetIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newStops = [...stops];
    const [draggedStop] = newStops.splice(draggedIndex, 1);
    newStops.splice(targetIndex, 0, draggedStop);

    const reorderedStops = newStops.map((stop, i) => ({
      ...stop,
      order: i + 1
    }));

    onStopsChange(reorderedStops);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  return (
    <div className="space-y-3">
      {/* Liste des étapes */}
      {stops.map((stop, index) => (
        <div
          key={index}
          draggable={!disabled}
          onDragStart={(e) => handleDragStart(e, index)}
          onDragOver={(e) => handleDragOver(e, index)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, index)}
          onDragEnd={handleDragEnd}
          className={`
            flex items-start gap-3 p-3 bg-white border rounded-lg transition-all
            ${draggedIndex === index ? 'opacity-50 scale-[0.98] border-dashed' : ''}
            ${dragOverIndex === index ? 'border-[#1db584] bg-green-50/30' : 'border-gray-200'}
            ${!disabled ? 'hover:border-[#1db584]/50 hover:shadow-sm' : ''}
            group
          `}
        >
          {/* Drag Handle */}
          {!disabled && (
            <div 
              className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 mt-2.5 transition-colors"
              aria-label="Réordonner l'étape"
            >
              <GripVertical className="h-5 w-5" />
            </div>
          )}

          {/* Numéro de l'étape */}
          <div className="flex-shrink-0 mt-2">
            <div className="w-7 h-7 rounded-full bg-[#1db584] text-white flex items-center justify-center text-sm font-semibold shadow-sm">
              {stop.order}
            </div>
          </div>

          {/* Contenu principal */}
          <div className="flex-1 space-y-2">
            {/* Adresse avec autocomplétion */}
            <div className="relative">
              <AddressInput
                value={stop.address}
                onChange={(text, coords) => handleAddressChange(index, text, coords)}
                placeholder={`Adresse de l'étape ${stop.order}`}
                disabled={disabled}
              />
              {stop.coordinates && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                  <MapPin className="h-4 w-4 text-[#1db584]" />
                </div>
              )}
            </div>

            {/* Instructions (optionnel) - design discret */}
            <Input
              value={stop.instructions || ''}
              onChange={(e) => handleInstructionsChange(index, e.target.value)}
              placeholder="Instructions (optionnel)"
              disabled={disabled}
              maxLength={150}
              className="h-8 text-sm bg-gray-50 border-gray-100 placeholder:text-gray-400 focus:bg-white"
            />
          </div>

          {/* Bouton supprimer */}
          {!disabled && (
            <button
              type="button"
              onClick={() => removeStop(index)}
              className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors mt-1.5"
              aria-label={`Supprimer l'étape ${stop.order}`}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      ))}

      {/* Bouton Ajouter une étape */}
      {stops.length < maxStops && (
        <Button
          type="button"
          variant="outline"
          onClick={addStop}
          disabled={disabled}
          className="w-full h-12 border-2 border-dashed border-gray-200 text-gray-500 hover:border-[#1db584] hover:text-[#1db584] hover:bg-green-50/50 transition-all gap-2"
        >
          <Plus className="h-4 w-4" />
          Ajouter une étape
          {stops.length > 0 && (
            <Badge variant="secondary" className="ml-2 text-xs">
              {stops.length}/{maxStops}
            </Badge>
          )}
        </Button>
      )}

      {/* Message limite atteinte */}
      {stops.length >= maxStops && (
        <p className="text-center text-sm text-amber-600 py-2">
          Limite de {maxStops} étapes atteinte
        </p>
      )}
    </div>
  );
};

export default MultiStopInput;
